import axios, {AxiosResponse} from 'axios'
import {API_URL, ID, Response} from '../../../../../../_metronic/helpers'
import {
  AutomaticInvoiceRatioChart,
  AutomaticInvoiceRatioChartResponse,
  AutomaticInvoiceRatioSettings,
  AutomaticInvoiceRatioSettingsResponse,
  AutomaticInvoiceRatioSummary,
  AutomaticInvoiceRatioSummaryResponse,
  AutomaticInvoiceVatClass,
  AutomaticInvoiceVatShareSource,
  AutomaticInvoiceVatShareStatus,
  AutomaticInvoiceVatShareStatusResponse,
  Product,
  ProductBulkAction,
  ProductBulkActionResponse,
  ProductBulkActionResult,
  ProductBulkAutomaticInvoiceUpdate,
  ProductBulkAutomaticInvoiceUpdateResponse,
  ProductBulkAutomaticInvoiceUpdateResult,
  ProductCategoriesResponse,
  ProductCategory,
  OnlineProductServicePriceFilters,
  ProductPrice,
  ProductPricesResponse,
  ProductServiceOnlinePrice,
  ProductServiceOnlinePriceMarketSummary,
  ProductServiceOnlinePriceMarketSummaryResponse,
  ProductServiceOnlinePricesResponse,
  ProductServicePriceSource,
  ProductServicePriceSourcesResponse,
  ProductsQueryResponse,
} from './_models'
import {
  InvoiceUsagePage,
  readInvoiceUsagePage,
} from '../../../shared/invoice-usage/invoiceUsageModels'

const PRODUCT_URL = `${API_URL}/product`
const GET_PRODUCTS_URL = `${API_URL}/products/query`
const PRODUCT_CATEGORIES_URL = `${API_URL}/productCategories`
const PRODUCT_PRICES_URL = `${API_URL}/productPrices`
const ONLINE_PRODUCT_SERVICE_PRICES_URL = `${API_URL}/service/onlineProductServicePrices`
const ONLINE_PRODUCT_SERVICE_PRICE_SOURCES_URL = `${API_URL}/service/onlineProductServicePriceSources`
const AUTOMATIC_INVOICE_RATIO_URL = `${API_URL}/products/automaticInvoiceRatio`
const AUTOMATIC_INVOICE_RATIO_CHART_URL = `${AUTOMATIC_INVOICE_RATIO_URL}/chart`
const AUTOMATIC_INVOICE_RATIO_SETTINGS_URL = `${AUTOMATIC_INVOICE_RATIO_URL}/settings`
const AUTOMATIC_INVOICE_VAT_SHARES_URL = `${AUTOMATIC_INVOICE_RATIO_URL}/vat-shares`
const AUTOMATIC_INVOICE_VAT_SHARE_STATUS_URL = `${AUTOMATIC_INVOICE_VAT_SHARES_URL}/status`
const AUTOMATIC_INVOICE_BULK_UPDATE_URL = `${API_URL}/products/automaticInvoice/bulk-update`

const mapAutomaticInvoiceTemplateSettings = (items: any[] | undefined | null) =>
  Array.isArray(items)
    ? items.map((item) => ({
        id:
          item.id ??
          item.ID ??
          item.productServiceAutomaticInvoiceTemplateSettingId ??
          item.ProductServiceAutomaticInvoiceTemplateSettingId,
        tmp: item.tmp ?? item.invoiceTemplate ?? item.InvoiceTemplate,
        wgp: item.wgp ?? item.wagePercent ?? item.WagePercent ?? 0,
        prp: item.prp ?? item.profitPercent ?? item.ProfitPercent ?? 0,
        cmp: item.cmp ?? item.commissionPercent ?? item.CommissionPercent ?? 0,
      }))
    : []

const getProducts = (query: string): Promise<ProductsQueryResponse> => {
  return axios
    .get(`${GET_PRODUCTS_URL}?${query}`)
    .then((d: AxiosResponse<ProductsQueryResponse>) => d.data)
}

const getProductById = (id: ID, invoiceDate?: string): Promise<Product | undefined> => {
  return axios
    .get(`${PRODUCT_URL}/${id}?invoiceDate=${invoiceDate || ''}`)
    .then((response: AxiosResponse<Response<Product>>) => response.data)
    .then((response: Response<Product>) => response.data)
}

const getProductVersions = (productId: ID): Promise<Product[]> => {
  return axios
    .get(`${API_URL}/products/subproducts/${productId}`)
    .then((response: AxiosResponse<Product[]>) => response.data || [])
}

const getProductInvoiceUsage = (
  productId: ID,
  page: number,
  itemsPerPage: number
): Promise<InvoiceUsagePage> => {
  return axios
    .get(`${PRODUCT_URL}/${productId}/invoices`, {params: {page, items_per_page: itemsPerPage}})
    .then((response: AxiosResponse<Response<InvoiceUsagePage>>) =>
      readInvoiceUsagePage(response.data?.data, page, itemsPerPage)
    )
}

const createProduct = (product: Product): Promise<Product | undefined> => {
  return axios
    .put(PRODUCT_URL, product)
    .then((response: AxiosResponse<Response<Product>>) => response.data)
    .then((response: Response<Product>) => response.data)
}

const updateProduct = (product: Product): Promise<Product | undefined> => {
  return axios
    .post(`${PRODUCT_URL}/${product.id}`, product)
    .then((response: AxiosResponse<Response<Product>>) => response.data)
    .then((response: Response<Product>) => response.data)
}

const deleteProduct = (productId: ID): Promise<void> => {
  return axios.post(`${PRODUCT_URL}/${productId}/delete`).then(() => {})
}

const archiveProduct = (productId: ID): Promise<Product | undefined> => {
  return axios
    .post(`${PRODUCT_URL}/${productId}/archive`)
    .then((response: AxiosResponse<Response<Product>>) => response.data)
    .then((response: Response<Product>) => response.data)
}

const restoreProduct = (productId: ID): Promise<Product | undefined> => {
  return axios
    .post(`${PRODUCT_URL}/${productId}/restore`)
    .then((response: AxiosResponse<Response<Product>>) => response.data)
    .then((response: Response<Product>) => response.data)
}

const setProductFavorite = (productId: ID, isFavorite: boolean): Promise<Product | undefined> => {
  return axios
    .post(`${PRODUCT_URL}/${productId}/favorite`, {fav: isFavorite})
    .then((response: AxiosResponse<Response<Product>>) => response.data)
    .then((response: Response<Product>) => response.data)
}

const deleteSelectedProducts = (productIds: Array<ID>): Promise<void> => {
  const requests = productIds.map((id) => axios.post(`${PRODUCT_URL}/${id}/delete`))
  return axios.all(requests).then(() => {})
}

const runProductBulkAction = (
  productIds: Array<ID>,
  action: ProductBulkAction
): Promise<ProductBulkActionResult | undefined> => {
  return axios
    .post(`${API_URL}/products/bulk-action`, {
      ids: productIds
        .filter((id) => id !== undefined && id !== null)
        .map((id) => Number(id)),
      action,
    })
    .then((response: AxiosResponse<ProductBulkActionResponse>) => response.data)
    .then((response: ProductBulkActionResponse) => response.data)
}

const updateSelectedProductsAutomaticInvoice = (
  productIds: Array<ID>,
  settings: ProductBulkAutomaticInvoiceUpdate
): Promise<ProductBulkAutomaticInvoiceUpdateResult | undefined> => {
  return axios
    .post(AUTOMATIC_INVOICE_BULK_UPDATE_URL, {
      ids: productIds
        .filter((id) => id !== undefined && id !== null)
        .map((id) => Number(id)),
      ...settings,
    })
    .then((response: AxiosResponse<ProductBulkAutomaticInvoiceUpdateResponse>) => response.data)
    .then((response: ProductBulkAutomaticInvoiceUpdateResponse) => response.data)
}

const getProductCategories = (search = ''): Promise<ProductCategory[]> => {
  return axios
    .get(`${PRODUCT_CATEGORIES_URL}?search=${encodeURIComponent(search)}`)
    .then((response: AxiosResponse<ProductCategoriesResponse | ProductCategory[] | any>) => {
      const payload = response.data
      const data = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.Data)
        ? payload.Data
        : []

      return data.map((item: any) => ({
        id: item.id ?? item.ID ?? item.productServiceCategoryId ?? item.ProductServiceCategoryId,
        nm: item.nm ?? item.name ?? item.Name,
        ds: item.ds ?? item.description ?? item.Description,
        pc: item.pc ?? item.productCount ?? item.ProductCount ?? 0,
        aiv: item.aiv ?? item.useInAutomaticInvoice ?? item.UseInAutomaticInvoice ?? false,
        air: item.air ?? item.automaticInvoiceRatio ?? item.AutomaticInvoiceRatio ?? 0,
        aqd:
          item.aqd ??
          item.automaticInvoiceQuantityDecimalPlaces ??
          item.AutomaticInvoiceQuantityDecimalPlaces ??
          8,
        qst:
          item.qst ??
          item.automaticInvoiceQuantityStep ??
          item.AutomaticInvoiceQuantityStep ??
          0.00000001,
        qmn:
          item.qmn ??
          item.automaticInvoiceMinQuantity ??
          item.AutomaticInvoiceMinQuantity ??
          0,
        qmx:
          item.qmx ??
          item.automaticInvoiceMaxQuantity ??
          item.AutomaticInvoiceMaxQuantity ??
          '',
        cair:
          item.cair ??
          item.childAutomaticInvoiceRatioTotal ??
          item.ChildAutomaticInvoiceRatioTotal ??
          0,
        pst: item.pst ?? item.priceSourceType ?? item.PriceSourceType ?? 1,
        mld: item.mld ?? item.maxLineDiscountPercent ?? item.MaxLineDiscountPercent ?? 5,
        aits: mapAutomaticInvoiceTemplateSettings(
          item.aits ??
            item.automaticInvoiceTemplateSettings ??
            item.AutomaticInvoiceTemplateSettings
        ),
        act: item.act ?? item.isActive ?? item.IsActive ?? true,
        dt: item.dt ?? item.createDateTime ?? item.CreateDateTime,
      }))
    })
}

const saveProductCategory = (category: ProductCategory): Promise<ProductCategory | undefined> => {
  const url = category.id ? `${PRODUCT_CATEGORIES_URL}/${category.id}` : PRODUCT_CATEGORIES_URL
  return axios
    .post(url, category)
    .then((response: AxiosResponse<Response<ProductCategory>>) => response.data)
    .then((response: Response<ProductCategory>) => response.data)
}

const deleteProductCategory = (categoryId: ID): Promise<void> => {
  return axios.post(`${PRODUCT_CATEGORIES_URL}/${categoryId}/delete`).then(() => {})
}

const getAutomaticInvoiceRatioSummaryData = (): Promise<AutomaticInvoiceRatioSummary> => {
  return axios
    .get(AUTOMATIC_INVOICE_RATIO_URL)
    .then((response: AxiosResponse<AutomaticInvoiceRatioSummaryResponse>) => response.data)
    .then((response: AutomaticInvoiceRatioSummaryResponse) => {
      const summary = response.data || {}
      const total = Number(summary.total || 0)

      return {
        ...summary,
        total: Number.isFinite(total) ? total : 0,
        hasSavedRatio: summary.hasSavedRatio ?? (total > 0),
      }
    })
}

const getAutomaticInvoiceRatioSummary = (): Promise<number> => {
  return getAutomaticInvoiceRatioSummaryData().then((summary) => Number(summary.total || 0))
}

const getAutomaticInvoiceRatioChart = (): Promise<AutomaticInvoiceRatioChart> => {
  return axios
    .get(AUTOMATIC_INVOICE_RATIO_CHART_URL)
    .then((response: AxiosResponse<AutomaticInvoiceRatioChartResponse>) => response.data)
    .then((response: AutomaticInvoiceRatioChartResponse) => {
      const chart = response.data || {}
      const total = Number(chart.total || 0)
      const items = Array.isArray(chart.items) ? chart.items : []

      return {
        ...chart,
        total: Number.isFinite(total) ? total : 0,
        hasSavedRatio: chart.hasSavedRatio ?? (total > 0 || items.length > 0),
        items: items.map((item) => ({
          key: item.key,
          label: item.label,
          value: Number(item.value || 0),
          type: item.type,
        })),
      }
    })
}

/** A number the server sent as a number or a numeric string; null for anything else. */
const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  return typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim()) ? Number(value) : null
}

const AUTOMATIC_INVOICE_VAT_SHARE_MONTH_COUNT = 12

/**
 * The twelve monthly exempt percents, فروردین first, with null for a month without a share (or a
 * value that is not a number); null when the server sent no list.
 */
const normalizeAutomaticInvoiceVatShareMonths = (value: unknown): (number | null)[] | null =>
  Array.isArray(value)
    ? Array.from({length: AUTOMATIC_INVOICE_VAT_SHARE_MONTH_COUNT}, (_, index) =>
        toNumberOrNull(value[index])
      )
    : null

const normalizeAutomaticInvoiceRatioSettings = (
  settings?: AutomaticInvoiceRatioSettings | any
): AutomaticInvoiceRatioSettings => {
  const periodType = Number(settings?.rpt ?? settings?.ratioPeriodType ?? 2)
  const minRowCount = Number(settings?.mrc ?? settings?.minInvoiceRowCount ?? 1)
  const normalizedMinRowCount =
    Number.isFinite(minRowCount) && minRowCount >= 1 ? Math.floor(minRowCount) : 1
  const maxRowCount = Number(settings?.xrc ?? settings?.maxInvoiceRowCount ?? normalizedMinRowCount)
  const normalizedMaxRowCount =
    Number.isFinite(maxRowCount) && maxRowCount >= normalizedMinRowCount
      ? Math.floor(maxRowCount)
      : normalizedMinRowCount

  return {
    ...(settings || {}),
    rpt: periodType >= 1 && periodType <= 4 ? periodType : 2,
    mrc: normalizedMinRowCount,
    xrc: normalizedMaxRowCount,
    // A server that predates the monthly shares sends no «vsa»: it stays undefined, and the panel
    // then shows nothing of the setting.
    vsa: typeof settings?.vsa === 'boolean' ? settings.vsa : undefined,
    vms: normalizeAutomaticInvoiceVatShareMonths(settings?.vms),
  }
}

const getAutomaticInvoiceRatioPeriodSettings = (): Promise<AutomaticInvoiceRatioSettings> => {
  return axios
    .get(AUTOMATIC_INVOICE_RATIO_SETTINGS_URL)
    .then((response: AxiosResponse<AutomaticInvoiceRatioSettingsResponse>) => response.data)
    .then((response: AutomaticInvoiceRatioSettingsResponse) =>
      normalizeAutomaticInvoiceRatioSettings(response.data)
    )
}

const saveAutomaticInvoiceRatioPeriodSettings = (
  settings: AutomaticInvoiceRatioSettings | number
): Promise<AutomaticInvoiceRatioSettings> => {
  const payload =
    typeof settings === 'number'
      ? {rpt: settings}
      : {
          rpt: settings.rpt,
          mrc: settings.mrc,
          xrc: settings.xrc,
        }

  return axios
    .post(AUTOMATIC_INVOICE_RATIO_SETTINGS_URL, payload)
    .then((response: AxiosResponse<AutomaticInvoiceRatioSettingsResponse>) => response.data)
    .then((response: AutomaticInvoiceRatioSettingsResponse) =>
      normalizeAutomaticInvoiceRatioSettings(response.data)
    )
}

/**
 * Saves the exempt percent of all twelve Jalali months, فروردین first; null leaves a month without
 * a share, and twelve nulls turn the shares off. The answer is the whole settings, as saved.
 */
const saveAutomaticInvoiceVatShares = (
  vms: (number | null)[]
): Promise<AutomaticInvoiceRatioSettings> => {
  return axios
    .post(AUTOMATIC_INVOICE_VAT_SHARES_URL, {vms})
    .then((response: AxiosResponse<AutomaticInvoiceRatioSettingsResponse>) => response.data)
    .then((response: AutomaticInvoiceRatioSettingsResponse) =>
      normalizeAutomaticInvoiceRatioSettings(response.data)
    )
}

const automaticInvoiceVatClasses: string[] = ['exempt', 'taxable', 'mixed', 'unknown']

const isAutomaticInvoiceVatClass = (value: string): value is AutomaticInvoiceVatClass =>
  automaticInvoiceVatClasses.includes(value)

/** A JSON object the server sent, read field by field; {} for anything that is not an object. */
const toServerRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const normalizeAutomaticInvoiceVatShareSource = (
  value: unknown,
  index: number
): AutomaticInvoiceVatShareSource => {
  const item = toServerRecord(value)
  const vatClass = String(item.vatClass ?? '').toLowerCase()
  const name = typeof item.name === 'string' ? item.name.trim() : ''

  return {
    key: String(item.key ?? `source-${index}`),
    kind: item.kind === 'product' ? 'product' : 'category',
    name: name || '-',
    ratio: toNumberOrNull(item.ratio) ?? 0,
    exemptCount: toNumberOrNull(item.exemptCount) ?? 0,
    taxableCount: toNumberOrNull(item.taxableCount) ?? 0,
    unknownCount: toNumberOrNull(item.unknownCount) ?? 0,
    vatClass: isAutomaticInvoiceVatClass(vatClass) ? vatClass : 'unknown',
  }
}

const normalizeAutomaticInvoiceVatShareStatus = (
  value: unknown,
  year: number,
  month: number
): AutomaticInvoiceVatShareStatus => {
  const status = toServerRecord(value)
  const reason = typeof status.reason === 'string' ? status.reason.trim() : ''

  return {
    year: toNumberOrNull(status.year) ?? year,
    month: toNumberOrNull(status.month) ?? month,
    monthName: typeof status.monthName === 'string' ? status.monthName : '',
    available: status.available !== false,
    applies: status.applies !== false,
    reason: reason || undefined,
    target: toNumberOrNull(status.target),
    exemptAmount: toNumberOrNull(status.exemptAmount) ?? 0,
    taxableAmount: toNumberOrNull(status.taxableAmount) ?? 0,
    exemptPercent: toNumberOrNull(status.exemptPercent),
    sources: Array.isArray(status.sources)
      ? status.sources.map(normalizeAutomaticInvoiceVatShareSource)
      : [],
    warnings: Array.isArray(status.warnings)
      ? status.warnings.filter(
          (warning: unknown): warning is string =>
            typeof warning === 'string' && warning.trim() !== ''
        )
      : [],
  }
}

/** One Jalali month's exempt share: its target, the counted sales so far and the sources by class. */
const getAutomaticInvoiceVatShareStatus = (
  year: number,
  month: number
): Promise<AutomaticInvoiceVatShareStatus> => {
  return axios
    .get(`${AUTOMATIC_INVOICE_VAT_SHARE_STATUS_URL}?year=${year}&month=${month}`)
    .then((response: AxiosResponse<AutomaticInvoiceVatShareStatusResponse>) =>
      normalizeAutomaticInvoiceVatShareStatus(response.data?.data, year, month)
    )
}

const getProductPrices = (params: {
  productServiceId?: ID
  categoryId?: ID
}): Promise<ProductPrice[]> => {
  const query = params.productServiceId
    ? `productServiceId=${params.productServiceId}`
    : `categoryId=${params.categoryId}`

  return axios
    .get(`${PRODUCT_PRICES_URL}?${query}`)
    .then((response: AxiosResponse<ProductPricesResponse>) => response.data)
    .then((response: ProductPricesResponse) => response.data || [])
}

const saveProductPrice = (price: ProductPrice): Promise<ProductPrice | undefined> => {
  const url = price.id ? `${PRODUCT_PRICES_URL}/${price.id}` : PRODUCT_PRICES_URL
  const payload = {...price}
  delete payload.td
  return axios
    .post(url, payload)
    .then((response: AxiosResponse<Response<ProductPrice>>) => response.data)
    .then((response: Response<ProductPrice>) => response.data)
}

const deleteProductPrice = (priceId: ID): Promise<void> => {
  return axios.delete(`${PRODUCT_PRICES_URL}/${priceId}`).then(() => {})
}

const getOnlineProductServicePrices = (
  priceSourceTypeOrFilters: number | OnlineProductServicePriceFilters,
  take = 30
): Promise<ProductServiceOnlinePrice[]> => {
  const filters =
    typeof priceSourceTypeOrFilters === 'number'
      ? {priceSourceType: priceSourceTypeOrFilters, take}
      : priceSourceTypeOrFilters

  const params = new URLSearchParams()
  if (filters.priceSourceType && filters.priceSourceType > 0) {
    params.set('priceSourceType', filters.priceSourceType.toString())
  }
  if (filters.from) {
    params.set('from', filters.from)
  }
  if (filters.to) {
    params.set('to', filters.to)
  }
  if (filters.search) {
    params.set('search', filters.search)
  }
  if (filters.page) {
    params.set('page', filters.page.toString())
  }
  if (filters.pageSize) {
    params.set('pageSize', filters.pageSize.toString())
  }
  params.set('take', (filters.take || take).toString())

  return axios
    .get(`${ONLINE_PRODUCT_SERVICE_PRICES_URL}?${params.toString()}`)
    .then((response: AxiosResponse<ProductServiceOnlinePricesResponse>) => response.data)
    .then((response: ProductServiceOnlinePricesResponse) => response.data || [])
}

const getOnlineProductServicePricesPaged = (
  filters: OnlineProductServicePriceFilters
): Promise<ProductServiceOnlinePricesResponse> => {
  const params = new URLSearchParams()
  if (filters.priceSourceType && filters.priceSourceType > 0) {
    params.set('priceSourceType', filters.priceSourceType.toString())
  }
  if (filters.from) {
    params.set('from', filters.from)
  }
  if (filters.to) {
    params.set('to', filters.to)
  }
  if (filters.search) {
    params.set('search', filters.search)
  }
  params.set('page', (filters.page || 1).toString())
  params.set('pageSize', (filters.pageSize || filters.take || 50).toString())

  return axios
    .get(`${ONLINE_PRODUCT_SERVICE_PRICES_URL}?${params.toString()}`)
    .then((response: AxiosResponse<ProductServiceOnlinePricesResponse>) => response.data)
}

const getOnlineProductServicePriceMarketSummary = (
  priceSourceType: number,
  days = 31
): Promise<ProductServiceOnlinePriceMarketSummary | undefined> => {
  return axios
    .get(
      `${ONLINE_PRODUCT_SERVICE_PRICES_URL}/marketSummary?priceSourceType=${priceSourceType}&days=${days}`
    )
    .then((response: AxiosResponse<ProductServiceOnlinePriceMarketSummaryResponse>) => response.data)
    .then((response: ProductServiceOnlinePriceMarketSummaryResponse) => response.data)
}

const getOnlineProductServicePriceSources = (): Promise<ProductServicePriceSource[]> => {
  return axios
    .get(ONLINE_PRODUCT_SERVICE_PRICE_SOURCES_URL)
    .then((response: AxiosResponse<ProductServicePriceSourcesResponse>) => response.data)
    .then((response: ProductServicePriceSourcesResponse) => response.data || [])
}

export {
  getProducts,
  deleteProduct,
  archiveProduct,
  restoreProduct,
  setProductFavorite,
  deleteSelectedProducts,
  runProductBulkAction,
  updateSelectedProductsAutomaticInvoice,
  getProductById,
  getProductVersions,
  getProductInvoiceUsage,
  createProduct,
  updateProduct,
  getProductCategories,
  saveProductCategory,
  deleteProductCategory,
  getAutomaticInvoiceRatioSummaryData,
  getAutomaticInvoiceRatioSummary,
  getAutomaticInvoiceRatioChart,
  getAutomaticInvoiceRatioPeriodSettings,
  saveAutomaticInvoiceRatioPeriodSettings,
  saveAutomaticInvoiceVatShares,
  getAutomaticInvoiceVatShareStatus,
  getProductPrices,
  saveProductPrice,
  deleteProductPrice,
  getOnlineProductServicePrices,
  getOnlineProductServicePricesPaged,
  getOnlineProductServicePriceMarketSummary,
  getOnlineProductServicePriceSources,
}
